import { DAILY_NAV_COLUMN_ORDER, DAILY_NAV_COLUMN_TYPES } from 'pages/Intelligence/DailyNav/dailyNav.constants';
import { shortDate } from 'utilities';
import { DailyNavColumn, DailyNavTableRow } from '../../useDailyNavSheet.types';

type ColumnGenerator = (rows: DailyNavTableRow[]) => DailyNavColumn;

const toOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildColumn = (
  rows: DailyNavTableRow[],
  getValue: (row: DailyNavTableRow) => string | number | null
): DailyNavColumn =>
  rows.reduce(
    (accumulator, row) => ({ ...accumulator, [String(row.companyId)]: { value: getValue(row) } }),
    {} as DailyNavColumn
  );

const columnGenerators: Record<string, ColumnGenerator> = {
  [DAILY_NAV_COLUMN_TYPES.VALUATION_DATE]: rows =>
    buildColumn(rows, row => shortDate(row.lookingGlass?.measurement_date_value) ?? null),

  [DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE]: rows =>
    buildColumn(rows, row => toOptionalNumber(row.lookingGlass?.equity_value)),

  // Weighted share value of the first common stock on the valuation's equity
  // allocation table, collected onto the Looking Glass snapshot.
  [DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE]: rows =>
    buildColumn(rows, row => toOptionalNumber(row.lookingGlass?.common_share_value)),

  // Averaged window values from the record's inline breakdown, as decimal-string
  // fractions (e.g. "0.021"). Null when no comp had data.
  [DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA]: rows =>
    buildColumn(rows, row => toOptionalNumber(row.record.breakdown?.short_window?.average)),

  [DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA]: rows =>
    buildColumn(rows, row => toOptionalNumber(row.record.breakdown?.long_window?.average)),

  [DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA]: rows =>
    buildColumn(rows, row => toOptionalNumber(row.record.breakdown?.since_last_valuation?.average)),

  // Value is unused for this column — createParser renders a ReviewPriceCell component instead.
  [DAILY_NAV_COLUMN_TYPES.STATUS]: rows => buildColumn(rows, () => null),
};

const createColumns = (rows: DailyNavTableRow[]): DailyNavColumn[] =>
  DAILY_NAV_COLUMN_ORDER.map(columnType => columnGenerators[columnType](rows));

export default createColumns;
